/**
 * Settings, persisted to localStorage.
 *
 * Everything here is a decision the user is entitled to make and we are not. The
 * storage budget in particular: an app that downloads audio has to say how much, in
 * megabytes, and let someone cap it — anything else is taking space from their phone
 * without asking.
 */

import { signal, effect } from '@preact/signals';

export type ThemeChoice = 'system' | 'light' | 'dark';

export interface Settings {
  /** Default sit length, seconds. Remembered from last time. */
  lastDurationSec: number;
  cadence: 'opening' | 'occasional' | 'frequent';
  /** Preferred spoken share; null keeps each recording's full reviewed boundaries. */
  desiredSpeechRatio: number | null;

  /** Bytes of audio we may keep on this device. */
  storageBudgetBytes: number;
  /** Keep sits available offline without being asked each time. */
  autoDownload: boolean;
  /** How many upcoming sits to keep downloaded when autoDownload is on. */
  keepAheadCount: number;

  /**
   * On by default, and it says exactly what it sends. Nothing in the beacon identifies
   * a person or names a recording, and it is one tap to turn off in Settings.
   */
  telemetryEnabled: boolean;
  /**
   * Whether the user has actually touched the diagnostics toggle.
   *
   * The stored settings blob is rewritten on every change, so every device on the old
   * build carries `telemetryEnabled: false` -- not because anyone declined, but because
   * that was the default and the effect persisted it. Merging defaults under stored
   * values would therefore leave the new default unreachable forever. This flag
   * separates "never asked" from "said no", and only a real toggle sets it.
   */
  telemetryChosen: boolean;

  /** Upload the full per-sit trace, including recording details and the user agent. */
  uploadDiagnostics: boolean;
  /** Whether the user has actually touched the trace-upload toggle. */
  uploadDiagnosticsChosen: boolean;
  /** Send every retained sit after each close instead of only the sit that just ended. */
  uploadFullLog: boolean;
  /** Whether the user has actually chosen the per-sit full-log policy. */
  uploadFullLogChosen: boolean;

  theme: ThemeChoice;
  /** Fade the screen to black during a sit. */
  dimDuringSit: boolean;
  /** Hold a wake lock so the sit is not interrupted by the device sleeping. */
  keepAwake: boolean;

  /** Theme filters the user has pinned in the library. */
  pinnedThemes: string[];

  /** Whether first-run onboarding has been completed. */
  onboarded: boolean;
  /**
   * When the install offer was last dismissed, and when the app was last installed.
   *
   * These were one boolean, and the boolean was a one-way door: it outlives an
   * uninstall, because uninstalling a PWA does not clear the origin's localStorage. So
   * someone who dismissed the banner once, installed later by another route, then
   * removed the app, was never offered it again by us -- and we suppress Chrome's own
   * offer, so they were never offered it at all. Two timestamps answer both questions
   * the boolean could not: has enough time passed, and did they install since.
   */
  installDismissedAt: number;
  installedAt: number;
}

/**
 * How long a dismissal lasts.
 *
 * An install nag that reappears every morning is exactly the kind of thing this app
 * should not do; one that never reappears is how the offer went missing. A month is
 * long enough that nobody sees it twice in a stretch of daily practice.
 */
export const INSTALL_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

/** Whether the install offer is currently suppressed by a dismissal. */
export function installDismissed(s: Settings, now = Date.now()): boolean {
  if (s.installDismissedAt <= 0) return false;
  // Installed since it was dismissed, and now uninstalled again -- the dismissal was
  // about a state that no longer holds.
  if (s.installedAt > s.installDismissedAt) return false;
  return now - s.installDismissedAt < INSTALL_DISMISS_MS;
}

export const STORAGE_PRESETS = [
  { label: 'Stream only', bytes: 0 },
  { label: '250 MB', bytes: 250e6 },
  { label: '500 MB', bytes: 500e6 },
  { label: '1 GB', bytes: 1e9 },
  { label: '2 GB', bytes: 2e9 },
  { label: '5 GB', bytes: 5e9 },
];

const DEFAULTS: Settings = {
  lastDurationSec: 25 * 60,
  cadence: 'occasional',
  desiredSpeechRatio: null,
  // 500 MB holds roughly a hundred sits at Opus speech-only sizes — the whole launch
  // corpus — while staying small enough not to be a rude default.
  storageBudgetBytes: 500e6,
  autoDownload: true,
  keepAheadCount: 3,
  // On by default. The beacon carries no identifier and never names a recording, and
  // without it we cannot tell whether the audio engine works on hardware we do not own
  // -- which is the one question this project has repeatedly guessed wrong about.
  telemetryEnabled: true,
  telemetryChosen: false,
  // Interrupted sits only become available after the next page load, so this has to be
  // the default rather than a switch someone is expected to find after the failure.
  uploadDiagnostics: true,
  uploadDiagnosticsChosen: false,
  // The owner's current investigation needs the complete device history after every sit,
  // including a predecessor that only gained its interrupted annotation on this launch.
  uploadFullLog: true,
  uploadFullLogChosen: false,
  theme: 'system',
  dimDuringSit: true,
  keepAwake: true,
  pinnedThemes: [],
  onboarded: false,
  installDismissedAt: 0,
  installedAt: 0,
};

const KEY = 'brahm.settings.v1';

/**
 * Fold a stored blob onto the defaults.
 *
 * Exported because it is the only part of loading with a decision in it, and that
 * decision has already been got wrong once: merging defaults *under* stored values
 * means a changed default can never reach a device that has run the app before, since
 * the effect below rewrites every field on every change. See `telemetryChosen`.
 */
export function mergeStored(raw: string | null): Settings {
  if (!raw) return { ...DEFAULTS };
  try {
    // Merge rather than replace, so a new setting added in a later version gets its
    // default instead of coming back undefined.
    const stored = JSON.parse(raw) as Partial<Settings>;
    const merged = { ...DEFAULTS, ...stored };
    // A stored value nobody chose must not outrank a changed default.
    if (!stored.telemetryChosen) merged.telemetryEnabled = DEFAULTS.telemetryEnabled;
    // This repeats the telemetryChosen treatment deliberately. The effect persists every
    // field, including defaults nobody selected; without a separate choice bit, changing
    // this default later would never reach a device that had opened an older build.
    if (!stored.uploadDiagnosticsChosen) {
      merged.uploadDiagnostics = DEFAULTS.uploadDiagnostics;
    }
    // Existing settings blobs already contain defaults nobody selected. Without a choice
    // bit, a future change to this investigation policy would strand those devices on the
    // old value in exactly the way telemetry was stranded off at launch.
    if (!stored.uploadFullLogChosen) merged.uploadFullLog = DEFAULTS.uploadFullLog;
    // Treat hand-edited/corrupt storage as no preference. A bad ratio must never grant
    // permission to crop speech or make every recording unreachable.
    if (merged.desiredSpeechRatio !== null
        && (typeof merged.desiredSpeechRatio !== 'number'
          || !Number.isFinite(merged.desiredSpeechRatio)
          || merged.desiredSpeechRatio <= 0
          || merged.desiredSpeechRatio > 1)) {
      merged.desiredSpeechRatio = null;
    }
    return merged;
  } catch {
    return { ...DEFAULTS };
  }
}

function load(): Settings {
  try {
    return mergeStored(localStorage.getItem(KEY));
  } catch {
    return { ...DEFAULTS };
  }
}

export const settings = signal<Settings>(load());

effect(() => {
  try { localStorage.setItem(KEY, JSON.stringify(settings.value)); } catch { /* private mode */ }
});

export function updateSettings(patch: Partial<Settings>): void {
  settings.value = { ...settings.value, ...patch };
}

export function resetSettings(): void {
  settings.value = { ...DEFAULTS };
}

/** Human-readable byte size, for the storage screen. */
export function formatBytes(n: number): string {
  if (n <= 0) return '0 MB';
  if (n < 1e6) return `${Math.round(n / 1e3)} KB`;
  if (n < 1e9) return `${Math.round(n / 1e6)} MB`;
  return `${(n / 1e9).toFixed(n < 1e10 ? 1 : 0)} GB`;
}

export function formatDuration(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/** "11 min spoken · 3 reminders · longest silence 14 min" */
export function formatPreview(p: {
  spokenSec: number; reminderCount: number; longestSilenceSec: number;
}): string {
  const bits = [`${Math.round(p.spokenSec / 60)} min spoken`];
  if (p.reminderCount > 0) {
    bits.push(`${p.reminderCount} reminder${p.reminderCount === 1 ? '' : 's'}`);
  }
  bits.push(`longest silence ${Math.round(p.longestSilenceSec / 60)} min`);
  return bits.join(' · ');
}
